import type React from 'react';
import { isValidUrl } from '../../../utils';
import type { HeroSlide } from './HeroSlides';
import type useUploadFileToS3 from '../../../hooks/useUploadFileToS3';

export const addSlideImage = async (options: {
  setError: (e: unknown) => any;
  updateSlides: (slides: Partial<HeroSlide>, index: number) => any;
  event: React.ChangeEvent<HTMLInputElement>;
  uploadFn: ReturnType<typeof useUploadFileToS3>['upload'];
  index: number;
}) => {
  const { setError, updateSlides, index, event, uploadFn } = options;

  const file = event.target.files?.[0];

  // No work to do
  if (!file) return;

  try {
    const fileExtension = file.name.split('.')[1];
    // File extensions are technically optional on unix systems. Didn't find anything in the web docs that says file extensions are required.
    if (!fileExtension)
      throw new Error('Invalid file provided. Please provide a file with a file extension.');

    const { url } = await uploadFn({
      file,
      kind: 'nationalHeroFolder',
      filename: `national_slide_${index}.${fileExtension}`,
    });

    updateSlides({ slideImage: url }, index);
  } catch (err) {
    setError(err);
  }
};

export const checkValidAndUpdate = async (options: {
  setError: (e: unknown) => any;
  updateHeroes: (slides: HeroSlide[], slidesToDelete: string[]) => any;
  slides: HeroSlide[];
  slidesToDelete: string[];
}) => {
  const { setError, slides = [], slidesToDelete, updateHeroes } = options;

  // make sure all necessary fields are present
  try {
    if (!updateHeroes) throw new Error('updateHeroes required in ~/HomepageForms/HeroSlides');
    slides?.forEach(
      (
        {
          slideImage,
          slideText,
          slideButtonText,
          slideLink,
          secondarySlideButtonText,
          secondarySlideLink,
        },
        idx
      ) => {
        if (!slideText || !slideButtonText || !slideLink) {
          throw new Error(`Missing text for slide ${idx + 1}`);
        }
        if (!slideImage) {
          throw new Error(`Missing image for slide ${idx + 1}`);
        }
        if (!isValidUrl(slideLink)) {
          throw new Error(`Invalid URL for slide ${idx + 1}`);
        }
        if (secondarySlideButtonText && !isValidUrl(secondarySlideLink)) {
          throw new Error(`Invalid second URL for slide ${idx + 1}`);
        }
      }
    );
    await updateHeroes(slides, slidesToDelete);
  } catch (err) {
    setError(err);
  }
};
